import { useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import mbLogo from '../assets/milwaukee-ballet-logo.png';
import agmaMark from '../assets/agma-mark.png';
import { apiFetch, } from '../utils/api';
import { authHeaders } from '../utils/auth';
import type { Role } from '../types';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get('expired') === '1';

  const submitOnEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.form?.requestSubmit();
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const res = await apiFetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data: { message?: string; token?: string; role?: Role } = await res.json();

      if (!res.ok) {
        setError(data.message || 'Login failed');
        return;
      }

      localStorage.setItem('token', data.token ?? '');
      localStorage.setItem('role', data.role ?? '');

      if (data.role === 'admin') navigate('/admin');
      else if (data.role === 'artistic_director') navigate('/review');
      else {
        // Land on Gallery tab if the artist has released content, otherwise My Photos
        try {
          const rel = await apiFetch('/api/gallery/released', { headers: authHeaders() });
          const relData = await rel.json() as { data: unknown[] };
          navigate(relData.data?.length > 0 ? '/portfolio?tab=gallery' : '/portfolio?tab=my-photos');
        } catch {
          navigate('/portfolio?tab=my-photos');
        }
      }
    } catch {
      setError('Network error — is the server running?');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='login-page'>
      <div className='login-card'>
        <div className='login-brand'>
          <img src={mbLogo} alt='Milwaukee Ballet' className='login-logo' />
          <h1 className='login-title'>PROSCENO</h1>
          <p className='login-subtitle'>Artist Photo Approval System</p>
        </div>

        {sessionExpired && !error && (
          <p className='login-notice' role='status'>Your session expired. Please log in again.</p>
        )}

        <form onSubmit={handleSubmit}>
          <div className='user-form-field'>
            <label htmlFor='email'>Email</label>
            <input
              id='email'
              type='email'
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={submitOnEnter}
              required
              autoComplete='email'
            />
          </div>
          <div className='user-form-field' style={{ marginTop: 16 }}>
            <label htmlFor='password'>Password</label>
            <div className='password-field-wrapper'>
              <input
                id='password'
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={submitOnEnter}
                required
                autoComplete='current-password'
              />
              <button
                type='button'
                className='password-toggle'
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                    <path d='M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24'/>
                    <line x1='1' y1='1' x2='23' y2='23'/>
                  </svg>
                ) : (
                  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                    <path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z'/>
                    <circle cx='12' cy='12' r='3'/>
                  </svg>
                )}
              </button>
            </div>
          </div>
          {error && <p className='login-error'>{error}</p>}
          <button type='submit' className='login-submit-btn' disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
          <div className='forgot-pw-footer'>
            <Link to='/forgot-password' className='forgot-pw-back-link'>Forgot password?</Link>
          </div>
        </form>
      </div>

      <div className='login-footer'>
        <img src={agmaMark} alt='AGMA' className='login-footer-mark' />
        <span>
          This system implements the photo approval rights guaranteed under the
          AGMA Collective Bargaining Agreement, Section 4, Article 41.
        </span>
      </div>
    </div>
  );
}
